import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from "date-fns";

import type { CalendarSource } from "../common/constants";
import type { CalendarItem } from "../types";

/** Local calendar day (`yyyy-MM-dd`) of an item; all-day items keep their stored date. */
export const dayKey = (item: Pick<CalendarItem, "all_day" | "start_at">) =>
  item.all_day
    ? item.start_at.slice(0, 10)
    : format(new Date(item.start_at), "yyyy-MM-dd");

/** Items of the enabled sources grouped by local day, each day sorted (all-day first, then by time). */
export const groupItemsByDay = (
  items: readonly CalendarItem[],
  sources: readonly CalendarSource[],
): Map<string, CalendarItem[]> => {
  const map = new Map<string, CalendarItem[]>();
  for (const item of items) {
    if (!sources.includes(item.source)) continue;
    const key = dayKey(item);
    map.set(key, [...(map.get(key) ?? []), item]);
  }
  for (const [key, dayItems] of map) {
    map.set(
      key,
      [...dayItems].sort(
        (a, b) =>
          Number(b.all_day) - Number(a.all_day) ||
          a.start_at.localeCompare(b.start_at),
      ),
    );
  }
  return map;
};

export interface MonthGridDay {
  key: string;
  date: Date;
  inMonth: boolean;
}

/** Full weeks (Monday first) covering the month of `cursor`, as rows of 7 days. */
export const buildMonthGrid = (cursor: Date): MonthGridDay[][] => {
  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 }),
  });
  const weeks: MonthGridDay[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(
      days.slice(i, i + 7).map((date) => ({
        key: format(date, "yyyy-MM-dd"),
        date,
        inMonth: isSameMonth(date, cursor),
      })),
    );
  }
  return weeks;
};

/** The Monday-first week of `cursor` as a single grid row (every day "in" the period). */
export const buildWeekGrid = (cursor: Date): MonthGridDay[][] => [
  eachDayOfInterval({
    start: startOfWeek(cursor, { weekStartsOn: 1 }),
    end: endOfWeek(cursor, { weekStartsOn: 1 }),
  }).map((date) => ({ key: format(date, "yyyy-MM-dd"), date, inMonth: true })),
];

/** At most `max` distinct sources of a day, in order of first appearance (the cell's dots). */
export const daySources = (
  dayItems: readonly CalendarItem[],
  max = 3,
): CalendarSource[] =>
  [...new Set(dayItems.map((item) => item.source))].slice(0, max);

/** Route opened when an item is tapped. */
export const itemPath = (item: CalendarItem): string => {
  switch (item.source) {
    case "event":
      return `/events/${item.record_id}`;
    case "team_task":
      return `/team_tasks/${item.record_id}`;
    case "task":
      // Internal (team) tasks have no contact: open the Tareas list.
      return item.contact_id ? `/contacts/${item.contact_id}/show` : "/tasks";
    case "deal_closing":
    case "milestone":
      return `/deals/${item.deal_id}/show`;
    case "renewal":
      return `/subscriptions/${item.record_id}`;
    case "invoice_due":
      return `/invoices/${item.record_id}`;
  }
};
